import type {JSX, ReactNode} from 'react';

import type {AfriexCountry} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import {getProcessorTxFieldsToDisplay} from './summaryFields';
import type {AfriexProcessorTransaction} from './types';

type TransactionSummaryProp = {
  data: AfriexProcessorTransaction;
  countries: AfriexCountry[];
};

export default function TransactionSummary({
  data,
  countries,
}: TransactionSummaryProp): JSX.Element {
  // Annotated here (rather than on `getProcessorTxFieldsToDisplay` itself,
  // which this screen does not own) so `Object.values(field)` below resolves
  // through the indexed-type overload instead of TypeScript's untyped
  // `Object.values(o: {}): any[]` fallback, which is what a union of
  // differently-keyed object literals falls back to.
  const fieldsToDisplay: Record<string, ReactNode>[] =
    getProcessorTxFieldsToDisplay(data, countries);
  return (
    <div className="bg-white rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      {/* Transaction Detail Header */}
      <>
        <div className="w-full flex justify-between items-center font-semibold">
          <h2>{capitalizeWords(data.type?.replace(/_/g, ' '))} Transaction</h2>
          <div className="flex gap-4 items-center">
            <div className="text-cyan-700 uppercase">
              {data.processor || 'N/A'}
            </div>
          </div>
        </div>
        <hr className="border-1 border-gray-200 mt-2 mb-5 w-full" />
      </>

      {/* Transaction Detail Display */}
      <div className="w-full">
        <div>
          {fieldsToDisplay?.map((field, idx) => {
            const key = Object.keys(field)[0];
            const value = Object.values(field)[0];

            if (!value || (typeof value === 'string' && value.length === 0)) {
              return null;
            }
            return (
              <div
                key={idx}
                className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 justify-between"
              >
                <p className="text-slate-500 text-sm">{key}</p>
                <p className="text-sm font-medium text-gray-700 w-2/3 overflow-hidden whitespace-normal">
                  {value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
